// src/pages/Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { apiService } from '../services/api';

// ─── Shared card style ───────────────────────────────────────────────────────
const card = {
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  padding: '1.25rem 1.5rem',
};

const sectionTitle = {
  fontSize: '0.95rem',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '1rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, color }) => (
  <div style={{ ...card, flex: '1 1 0', minWidth: '140px' }}>
    <div style={{ fontSize: '0.8rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.4rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '2rem', fontWeight: '700', color: color || '#111827' }}>
      {value?.toLocaleString() ?? '–'}
    </div>
  </div>
);

// ─── Horizontal bar chart (div-based) ─────────────────────────────────────────
const HBarChart = ({ data, labelKey, valueKey, color }) => {
  if (!data || data.length === 0) {
    return <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No data</div>;
  }
  const max = Math.max(...data.map(d => d[valueKey]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {data.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{
            width: '130px',
            fontSize: '0.8rem',
            color: '#374151',
            textAlign: 'right',
            flexShrink: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }} title={item[labelKey]}>
            {item[labelKey]}
          </div>
          <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '3px', height: '16px', overflow: 'hidden' }}>
            <div style={{
              width: `${(item[valueKey] / max) * 100}%`,
              height: '100%',
              background: color || '#3b82f6',
              borderRadius: '3px',
              transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', width: '36px', textAlign: 'right', flexShrink: 0 }}>
            {item[valueKey]}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Daily sparkline bar chart ────────────────────────────────────────────────
const SparklineChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No data</div>;
  }
  const max = Math.max(...data.map(d => d.count), 1);
  const CHART_HEIGHT = 80; // px available for the bars (label sits above)

  return (
    <div>
      {/* bars + count labels */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: `${CHART_HEIGHT + 16}px` }}>
        {data.map((item, i) => {
          const barHeight = Math.max((item.count / max) * CHART_HEIGHT, 3);
          return (
            <div
              key={i}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
              }}
            >
              {/* count label above bar — only show when non-zero */}
              <div style={{ fontSize: '0.6rem', color: '#6b7280', lineHeight: 1, marginBottom: '2px', height: '10px' }}>
                {item.count > 0 ? item.count : ''}
              </div>
              <div
                title={`${item.date}: ${item.count}`}
                style={{
                  width: '100%',
                  height: `${barHeight}px`,
                  background: '#3b82f6',
                  borderRadius: '2px 2px 0 0',
                  transition: 'height 0.4s ease',
                }}
              />
            </div>
          );
        })}
      </div>
      {/* x-axis date labels — show every ~4th to avoid crowding */}
      <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
        {data.map((item, i) => {
          const showLabel = i === 0 || i === data.length - 1 || i % 4 === 0;
          const [, month, day] = item.date.split('-');
          return (
            <div key={i} style={{ flex: 1, fontSize: '0.65rem', color: '#9ca3af', textAlign: 'center' }}>
              {showLabel ? `${month}/${day}` : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Health dot ───────────────────────────────────────────────────────────────
const StatusDot = ({ status }) => {
  const color = status === 'ok' ? '#10b981' : status === 'error' ? '#ef4444' : '#f59e0b';
  return (
    <span style={{
      display: 'inline-block',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: color,
      marginRight: '6px',
    }} />
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────
const Skeleton = ({ width = '100%', height = '20px', style = {} }) => (
  <div style={{
    width, height,
    background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
    borderRadius: '4px',
    ...style,
  }} />
);

// ─── Main component ───────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [health, setHealth] = useState(null);
  const [healthChecking, setHealthChecking] = useState(true);
  const healthIntervalRef = useRef(null);

  // Fetch stats once on mount
  useEffect(() => {
    apiService.getDashboardStats()
      .then(data => { setStats(data); setStatsLoading(false); })
      .catch(err => { setStatsError(err.message); setStatsLoading(false); });
  }, []);

  // Health polling — fetch immediately, then every 30 s
  useEffect(() => {
    const fetchHealth = () => {
      setHealthChecking(true);
      apiService.getHealthCheck()
        .then(data => { setHealth(data); setHealthChecking(false); })
        .catch(() => {
          setHealth({ status: 'degraded', checks: { api: { status: 'ok' }, mongodb: { status: 'error', detail: 'Unreachable' } } });
          setHealthChecking(false);
        });
    };

    fetchHealth();
    healthIntervalRef.current = setInterval(fetchHealth, 30000);
    return () => clearInterval(healthIntervalRef.current);
  }, []);

  const s = stats || {};
  const sc = s.status_counts || {};

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <Navbar onNavigate={path => navigate(path)} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1.75rem' }}>
          Platform Dashboard
        </h1>

        {statsError && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1.5rem' }}>
            Failed to load statistics: {statsError}
          </div>
        )}

        {/* ── KPI cards ── */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {statsLoading ? (
            [0,1,2,3].map(i => (
              <div key={i} style={{ ...card, flex: '1 1 0', minWidth: '140px' }}>
                <Skeleton height="12px" width="60%" style={{ marginBottom: '0.6rem' }} />
                <Skeleton height="32px" width="50%" />
              </div>
            ))
          ) : (
            <>
              <KpiCard label="Total Analyses" value={s.total} />
              <KpiCard label="Completed" value={sc.completed} color="#059669" />
              <KpiCard label="Analyzing" value={s.total - (sc.completed || 0) - (sc.failed || 0)} color="#2563eb" />
              <KpiCard label="Failed" value={sc.failed} color="#dc2626" />
            </>
          )}
        </div>

        {/* ── Two-column row: families + daily activity ── */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>

          {/* Malware Family Distribution */}
          <div style={{ ...card, flex: '1 1 340px' }}>
            <div style={sectionTitle}>Malware Family Distribution</div>
            {statsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[0,1,2,3,4].map(i => <Skeleton key={i} height="16px" />)}
              </div>
            ) : (
              <HBarChart
                data={s.malware_families || []}
                labelKey="family"
                valueKey="count"
                color="#6366f1"
              />
            )}
          </div>

          {/* Daily Upload Activity */}
          <div style={{ ...card, flex: '1 1 280px' }}>
            <div style={sectionTitle}>Daily Upload Activity (last 14 days)</div>
            {statsLoading ? (
              <Skeleton height="80px" />
            ) : (
              <SparklineChart data={s.daily_activity || []} />
            )}
          </div>
        </div>

        {/* ── Two-column row: tag distribution + health ── */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>

          {/* Tag Distribution */}
          <div style={{ ...card, flex: '1 1 340px' }}>
            <div style={sectionTitle}>Tag Distribution (top 10)</div>
            {statsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[0,1,2,3].map(i => <Skeleton key={i} height="16px" />)}
              </div>
            ) : (
              <HBarChart
                data={s.tag_distribution || []}
                labelKey="tag"
                valueKey="count"
                color="#0891b2"
              />
            )}
          </div>

          {/* System Health */}
          <div style={{ ...card, flex: '0 1 280px', minWidth: '240px' }}>
            <div style={sectionTitle}>System Health</div>
            {healthChecking && !health ? (
              <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Checking...</div>
            ) : health ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    background: health.status === 'ok' ? '#d1fae5' : '#fee2e2',
                    color: health.status === 'ok' ? '#065f46' : '#991b1b',
                  }}>
                    {health.status?.toUpperCase()}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {Object.entries(health.checks || {}).map(([name, check]) => (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#374151' }}>
                        <StatusDot status={check.status} />
                        {name.charAt(0).toUpperCase() + name.slice(1)}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {check.latency_ms != null ? `${check.latency_ms} ms` : check.detail || check.status}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '1rem', fontSize: '0.7rem', color: '#9ca3af' }}>
                  {healthChecking ? 'Checking...' : `Last checked: ${health.timestamp ? new Date(health.timestamp).toLocaleTimeString() : '–'}`}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
